import React, { useState, useEffect } from 'react';
import Sidebar from '../components/dashboard/Sidebar';
import { useRecruiters } from '../context/RecruiterContext';
import { useUser } from '../context/UserContext';
import { Bar, Line } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend } from 'chart.js';
import { FiActivity, FiClock, FiFileText, FiUsers, FiSearch, FiCalendar, FiDownload } from 'react-icons/fi';

// Register ChartJS components
ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend);

const RecruiterPerformancePage = () => {
  const { recruiters, loading, getRecruiterPerformance } = useRecruiters();
  const { user } = useUser();
  const [selectedRecruiter, setSelectedRecruiter] = useState(null);
  const [performance, setPerformance] = useState(null);
  const [performanceLoading, setPerformanceLoading] = useState(false);
  
  // Get timeRange from URL or default to 'week'
  const [timeRange, setTimeRange] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('timeRange') || 'week';
  });

  useEffect(() => {
    // If there's only one recruiter, select it automatically
    if (recruiters.length === 1) {
      setSelectedRecruiter(recruiters[0]);
    }
  }, [recruiters]);

  useEffect(() => {
    const fetchPerformance = async () => {
      if (selectedRecruiter) {
        setPerformanceLoading(true);

        const data = await getRecruiterPerformance(selectedRecruiter.id);

        if (data) {

          setPerformance(data);
        } else {
          console.error('[ERROR] No performance data returned from backend');
          setPerformance(null);
        }
        
        setPerformanceLoading(false);
      }
    };

    fetchPerformance();
  }, [selectedRecruiter, timeRange, getRecruiterPerformance]);

  // Use only real performance data - no mock fallback
  const performanceData = performance;

  // Download report function for ALL recruiters
  const downloadReport = async () => {
    if (!recruiters || recruiters.length === 0) {
      alert('No recruiters available to download report');
      return;
    }

    try {
      // Prepare CSV data
      const csvData = [];
      csvData.push(['All Recruiters Performance Report']);
      csvData.push(['Time Range:', timeRange.charAt(0).toUpperCase() + timeRange.slice(1)]);
      csvData.push(['Report Generated:', new Date().toLocaleString()]);
      csvData.push(['']);
      csvData.push(['Summary Table']);
      csvData.push(['Recruiter Name', 'Email', 'Status', 'Jobs Posted', 'Active Time', 'Login Hours (Total)', 'Candidates Reviewed', 'AI Credits Used']);
      
      // Fetch performance for each recruiter
      for (const recruiter of recruiters) {
        try {
          const perfData = await getRecruiterPerformance(recruiter.id);
          if (perfData) {
            const loginHours = perfData.loginHours?.total || recruiter.loginHours?.total || 0;
            csvData.push([
              recruiter.name,
              recruiter.email,
              recruiter.status,
              perfData.totalJobsPosted || 0,
              formatMinutes(perfData.totalActiveTime || 0),
              `${loginHours.toFixed(1)}h`,
              perfData.candidatesReviewed || 0,
              perfData.totalAiCreditsUsed || 0
            ]);
          }
        } catch (error) {
          console.error(`Error fetching performance for ${recruiter.name}:`, error);
          csvData.push([
            recruiter.name,
            recruiter.email,
            recruiter.status,
            'N/A',
            'N/A',
            'N/A',
            'N/A',
            'N/A'
          ]);
        }
      }

      // Convert to CSV string
      const csvContent = csvData.map(row => row.join(',')).join('\n');
      
      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      const fileName = `All_Recruiters_Performance_${timeRange}_${new Date().toISOString().split('T')[0]}.csv`;
      
      link.setAttribute('href', url);
      link.setAttribute('download', fileName);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

    } catch (error) {
      console.error('Error generating report:', error);
      alert('Failed to generate report. Please try again.');
    }
  };

  // Chart data for jobs posted - only render if we have data
  const jobsPostedChartData = performanceData ? {
    labels: performanceData.labels || [],
    datasets: [
      {
        label: 'Jobs Posted',
        data: performanceData.jobsPosted || [],
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        borderColor: 'rgba(0, 0, 0, 1)',
        borderWidth: 1},
    ]} : null;

  // Chart data for active time - only render if we have data
  const activeTimeChartData = performanceData ? {
    labels: performanceData.labels || [],
    datasets: [
      {
        label: 'Active Time (minutes)',
        data: performanceData.activeTime || [],
        backgroundColor: 'rgba(255, 204, 0, 0.8)',
        borderColor: 'rgba(255, 204, 0, 1)',
        borderWidth: 1,
        tension: 0.4
      }
    ]
  } : null;

  // Chart data for AI credits used - only render if we have data
  const aiCreditsChartData = performanceData ? {
    labels: performanceData.labels || [],
    datasets: [
      {
        label: 'AI Credits Used',
        data: performanceData.aiCreditsUsed || [], // Fixed to use aiCreditsUsed (matches backend)
        backgroundColor: 'rgba(54, 162, 235, 0.8)',
        borderColor: 'rgba(54, 162, 235, 1)',
        borderWidth: 1},
    ]} : null;

  // Chart data for Login Hours - only render if we have data
  const loginHoursChartData = performanceData ? {
    labels: performanceData.labels || [],
    datasets: [
      {
        label: 'Login Hours',
        data: performanceData.loginHours || [], // Daily login hours from backend
        backgroundColor: 'rgba(102, 126, 234, 0.8)',
        borderColor: 'rgba(102, 126, 234, 1)',
        borderWidth: 1,
        tension: 0.4
      }
    ]
  } : null;

  // Chart options
  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top'
      },
      title: {
        display: false
      }
    },
    scales: {
      y: {
        beginAtZero: true
      }
    }
  };

  // Format minutes to hours and minutes
  const formatMinutes = (minutes) => {
    if (!minutes || isNaN(minutes)) {
      return '0h 0m'; // Return default value for undefined, null, or NaN
    }
    const hours = Math.floor(minutes / 60);
    const mins = Math.floor(minutes % 60);
    return `${hours}h ${mins}m`;
  };

  return (
    <div className="flex bg-white min-h-screen font-sans">
      <Sidebar />
      <main className="flex-1 p-8 ml-0 md:ml-24">
        <header className="mb-8 animate-fade-in transition-all duration-300">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold text-black">{user?.name || 'Employer'}</h1>
          </div>
        </header>
        <div className="mt-8 bg-black p-6 border border-black rounded-lg" style={{ boxShadow: 'rgba(255, 255, 255, 0.8) 3px 3px 0px', transform: 'translateX(-3px) translateY(-3px)' }}>
          <div>
            <h1 className="text-3xl font-bold text-white">Recruiter Performance</h1>
            <p className="text-white mt-1">Track and analyze your recruiters' activity and performance</p>
          </div>
        </div>
        <div className="mt-6 bg-white p-8 border border-black rounded-lg" style={{ boxShadow: 'rgba(0, 0, 0, 0.8) 3px 3px 0px', transform: 'translateX(-3px) translateY(-3px)' }}>
          <div className="flex flex-col lg:flex-row lg:justify-between lg:items-end space-y-4 lg:space-y-0 lg:space-x-4">
            <div className="w-full lg:w-1/3">
              <label className="block text-sm font-medium text-black mb-2">Select Recruiter</label>
              <select 
                className="w-full px-4 py-3 border border-black focus:outline-none transition-all bg-white"
                value={selectedRecruiter?.id || ''}
                onChange={(e) => {
                  const selected = recruiters.find(r => r.id === e.target.value);
                  setSelectedRecruiter(selected || null);
                }}
              >
                <option value="">Select a recruiter</option>
                {recruiters.map(recruiter => (
                  <option key={recruiter.id} value={recruiter.id}>
                    {recruiter.name} ({recruiter.email})
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-black mb-2">Time Range</label>
              <div className="flex space-x-2">
                <button 
                  onClick={() => {
                    setTimeRange('week');
                    // Update URL with timeRange parameter
                    const url = new URL(window.location.href);
                    url.searchParams.set('timeRange', 'week');
                    window.history.replaceState({}, '', url);
                  }}
                  className={`px-4 py-2 border ${timeRange === 'week' ? 'bg-black text-white' : 'border-black text-black'}`}
                >
                  Week
                </button>
                <button 
                  onClick={() => {
                    setTimeRange('month');
                    // Update URL with timeRange parameter
                    const url = new URL(window.location.href);
                    url.searchParams.set('timeRange', 'month');
                    window.history.replaceState({}, '', url);
                  }}
                  className={`px-4 py-2 border ${timeRange === 'month' ? 'bg-black text-white' : 'border-black text-black'}`}
                >
                  Month
                </button>
                <button 
                  onClick={() => {
                    setTimeRange('quarter');
                    // Update URL with timeRange parameter
                    const url = new URL(window.location.href);
                    url.searchParams.set('timeRange', 'quarter');
                    window.history.replaceState({}, '', url);
                  }}
                  className={`px-4 py-2 border ${timeRange === 'quarter' ? 'bg-black text-white' : 'border-black text-black'}`}
                >
                  Quarter
                </button>
                <button 
                  onClick={() => {
                    setTimeRange('year');
                    // Update URL with timeRange parameter
                    const url = new URL(window.location.href);
                    url.searchParams.set('timeRange', 'year');
                    window.history.replaceState({}, '', url);
                  }}
                  className={`px-4 py-2 border ${timeRange === 'year' ? 'bg-black text-white' : 'border-black text-black'}`}
                >
                  Year
                </button>
              </div>
            </div>
            
            <div className="flex items-center">
              <button
                onClick={downloadReport}
                disabled={!recruiters || recruiters.length === 0}
                className={`flex items-center space-x-2 px-6 py-3 border border-black whitespace-nowrap ${
                  recruiters && recruiters.length > 0
                    ? 'bg-black text-white hover:bg-gray-800 cursor-pointer' 
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                } transition-all`}
                style={{ boxShadow: recruiters && recruiters.length > 0 ? 'rgba(0, 0, 0, 0.3) 2px 2px 0px' : 'none' }}
              >
                <FiDownload size={18} />
                <span>Download All Reports</span>
              </button>
            </div>
          </div>
        </div>

        {loading || !selectedRecruiter ? (
          <div className="mt-8 bg-white p-8 border border-black rounded-lg flex justify-center items-center" style={{ boxShadow: 'rgba(0, 0, 0, 0.8) 3px 3px 0px', transform: 'translateX(-3px) translateY(-3px)', minHeight: '400px' }}>
            {loading ? (
              <div className="flex flex-col items-center">
                <svg className="animate-spin h-10 w-10 text-black mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <p className="text-gray-600">Loading recruiters...</p>
              </div>
            ) : (
              <div className="text-center">
                <FiUsers size={48} className="mx-auto text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900">Select a Recruiter</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Choose a recruiter from the dropdown to view their performance metrics
                </p>
              </div>
            )}
          </div>
        ) : !performanceData && !performanceLoading ? (
          <div className="mt-8 bg-white p-8 border border-black rounded-lg flex justify-center items-center" style={{ boxShadow: 'rgba(0, 0, 0, 0.8) 3px 3px 0px', transform: 'translateX(-3px) translateY(-3px)', minHeight: '400px' }}>
            <div className="text-center">
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Performance Data Available</h3>
              <p className="text-sm text-gray-500">
                Unable to load performance data for this recruiter. Please try again later.
              </p>
            </div>
          </div>
        ) : performanceData ? (
          <>
            {/* Performance Summary */}
            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
              <div className="bg-white p-6 border border-black rounded-lg" style={{ boxShadow: 'rgba(0, 0, 0, 0.8) 3px 3px 0px', transform: 'translateX(-3px) translateY(-3px)' }}>
                <div className="flex items-center">
                  <div className="p-3 bg-yellow-100 rounded-full mr-4">
                    <FiFileText className="text-yellow-600" size={24} />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Jobs Posted</p>
                    <h3 className="text-2xl font-bold">{performanceData.totalJobsPosted}</h3>
                  </div>
                </div>
              </div>
              
              <div className="bg-white p-6 border border-black rounded-lg" style={{ boxShadow: 'rgba(0, 0, 0, 0.8) 3px 3px 0px', transform: 'translateX(-3px) translateY(-3px)' }}>
                <div className="flex items-center">
                  <div className="p-3 bg-blue-100 rounded-full mr-4">
                    <FiClock className="text-blue-600" size={24} />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Active Time</p>
                    <h3 className="text-2xl font-bold">{formatMinutes(performanceData.totalActiveTime)}</h3>
                  </div>
                </div>
              </div>
              
              <div className="bg-white p-6 border border-black rounded-lg" style={{ boxShadow: 'rgba(0, 0, 0, 0.8) 3px 3px 0px', transform: 'translateX(-3px) translateY(-3px)' }}>
                <div className="flex items-center">
                  <div className="p-3 bg-green-100 rounded-full mr-4">
                    <FiUsers className="text-green-600" size={24} />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Candidates Reviewed</p>
                    <h3 className="text-2xl font-bold">{performanceData.candidatesReviewed}</h3>
                  </div>
                </div>
              </div>
              
              <div className="bg-white p-6 border border-black rounded-lg" style={{ boxShadow: 'rgba(0, 0, 0, 0.8) 3px 3px 0px', transform: 'translateX(-3px) translateY(-3px)' }}>
                <div className="flex items-center">
                  <div className="p-3 bg-purple-100 rounded-full mr-4">
                    <FiActivity className="text-purple-600" size={24} />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">AI Credits Used</p>
                    <h3 className="text-2xl font-bold">{performanceData.totalAiCreditsUsed || 0}</h3>
                  </div>
                </div>
              </div>
              
              <div className="bg-white p-6 border border-black rounded-lg" style={{ boxShadow: 'rgba(0, 0, 0, 0.8) 3px 3px 0px', transform: 'translateX(-3px) translateY(-3px)' }}>
                <div className="flex items-center">
                  <div className="p-3 bg-indigo-100 rounded-full mr-4">
                    <FiClock className="text-indigo-600" size={24} />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Login Hours ({timeRange.charAt(0).toUpperCase() + timeRange.slice(1)})</p>
                    <h3 className="text-2xl font-bold">
                      {(performanceData.loginHours?.total || selectedRecruiter?.loginHours?.total || 0).toFixed(1)}h
                    </h3>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Charts */}
            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white p-6 border border-black rounded-lg" style={{ boxShadow: 'rgba(0, 0, 0, 0.8) 3px 3px 0px', transform: 'translateX(-3px) translateY(-3px)' }}>
                <h3 className="text-lg font-bold text-black mb-4">Jobs Posted</h3>
                <div className="h-64">
                  <Bar data={jobsPostedChartData} options={chartOptions} />
                </div>
              </div>
              
              <div className="bg-white p-6 border border-black rounded-lg" style={{ boxShadow: 'rgba(0, 0, 0, 0.8) 3px 3px 0px', transform: 'translateX(-3px) translateY(-3px)' }}>
                <h3 className="text-lg font-bold text-black mb-4">Active Time</h3>
                <div className="h-64">
                  <Line data={activeTimeChartData} options={chartOptions} />
                </div>
              </div>
              
              <div className="bg-white p-6 border border-black rounded-lg" style={{ boxShadow: 'rgba(0, 0, 0, 0.8) 3px 3px 0px', transform: 'translateX(-3px) translateY(-3px)' }}>
                <h3 className="text-lg font-bold text-black mb-4">AI Credits Used</h3>
                <div className="h-64">
                  <Bar data={aiCreditsChartData} options={chartOptions} />
                </div>
              </div>
              
              <div className="bg-white p-6 border border-black rounded-lg" style={{ boxShadow: 'rgba(0, 0, 0, 0.8) 3px 3px 0px', transform: 'translateX(-3px) translateY(-3px)' }}>
                <h3 className="text-lg font-bold text-black mb-4">Login Hours (Daily)</h3>
                <div className="h-64">
                  <Line data={loginHoursChartData} options={chartOptions} />
                </div>
              </div>
            </div>
            
            {/* Activity Details Section */}
            <div className="mt-8 bg-white p-6 border border-black rounded-lg" style={{ boxShadow: 'rgba(0, 0, 0, 0.8) 3px 3px 0px', transform: 'translateX(-3px) translateY(-3px)' }}>
              <h3 className="text-lg font-bold text-black mb-4">Activity Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {performanceLoading ? (
                  <div className="flex justify-center items-center h-40 col-span-2">
                    <svg className="animate-spin h-8 w-8 text-black" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </div>
                ) : (
                  <>
                    <div className="flex justify-between items-center p-3 bg-gray-50 rounded">
                      <div className="flex items-center">
                        <FiClock className="text-gray-500 mr-3" />
                        <span className="text-gray-700">Average Response Time</span>
                      </div>
                      <span className="font-medium">{performanceData.averageResponseTime || 0} minutes</span>
                    </div>
                    
                    <div className="flex justify-between items-center p-3 bg-gray-50 rounded">
                      <div className="flex items-center">
                        <FiUsers className="text-gray-500 mr-3" />
                        <span className="text-gray-700">Candidates Reviewed</span>
                      </div>
                      <span className="font-medium">{performanceData.candidatesReviewed}</span>
                    </div>
                      
                      <div className="flex justify-between items-center p-3 bg-gray-50 rounded">
                        <div className="flex items-center">
                          <FiCalendar className="text-gray-500 mr-3" />
                          <span className="text-gray-700">Active Days</span>
                        </div>
                        <span className="font-medium">
                          {(performanceData.activeTime || []).filter(minutes => minutes > 0).length} days
                        </span>
                      </div>
                      
                      <div className="flex justify-between items-center p-3 bg-gray-50 rounded">
                        <div className="flex items-center">
                          <FiSearch className="text-gray-500 mr-3" />
                          <span className="text-gray-700">Database Searches</span>
                        </div>
                        <span className="font-medium">{performanceData.databaseSearches || 0}</span>
                      </div>
                    </>
                  )}
              </div>
            </div>
          </>
        ) : null}
      </main>
    </div>
  );
};

export default RecruiterPerformancePage;